import AdminLayout from '@/components/admin/AdminLayout'
import MetricCard from '@/components/admin/MetricCard'
import RecentOrders from '@/components/admin/RecentOrders'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useDashboard } from '@/queries/useDashboard'
import { clearSession } from '@/utils/clearSession'
import { useUser, useClerk } from '@clerk/clerk-react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Store,
  Building2,
  UtensilsCrossed,
  Users,
  ShoppingCart,
  DollarSign,
} from 'lucide-react'

export default function Dashboard() {
  const { t } = useTranslation('admin')
  const [searchParams] = useSearchParams()
  const { user } = useUser()
  const clerk = useClerk()

  // Detect user role from Clerk metadata
  const userRoles = (user?.publicMetadata?.roles as string[] | undefined) || []
  const isSuperAdmin = Array.isArray(userRoles) && userRoles.includes('super_admin')

  // Read URL query parameters
  const restaurantIdFromUrl = searchParams.get('restaurantId')
  const branchIdFromUrl = searchParams.get('branchId')

  // For branch manager, ignore URL params (always use token context)
  // For restaurant admin, ignore restaurantId (always use token context)
  // For super admin, use URL params if provided
  const effectiveRestaurantId =
    isSuperAdmin ? restaurantIdFromUrl || null : null

  const effectiveBranchId = branchIdFromUrl || null

  // Fetch dashboard data with context
  const { data, isLoading, error } = useDashboard(effectiveRestaurantId, effectiveBranchId)

  // Debug: Log dashboard data to see what we're receiving
  console.log('[Dashboard] Dashboard data:', {
    data,
    restaurantsCount: data?.restaurants?.count,
    isSuperAdmin,
    effectiveRestaurantId,
    effectiveBranchId,
  })

  // Extract scope for metric labels
  const scope = data?.context?.scope || 'branch' // Default to branch if no context

  return (
    <AdminLayout>
      <div
        className="container mx-auto p-6"
        style={{
          padding: 'var(--spacing-lg)',
          maxWidth: '1280px',
          backgroundColor: 'var(--background)',
          minHeight: '100vh',
        }}
        data-testid="dashboard-page"
      >
        <div
          className="mb-6"
          style={{
            marginBottom: 'var(--spacing-lg)',
          }}
        >
          <h1
            className="text-3xl font-bold"
            style={{
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              fontFamily: 'var(--font-heading)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--spacing-sm)',
            }}
            data-testid="dashboard-title"
          >
            {t('dashboard.title')}
          </h1>
          <p
            style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-base)',
              fontFamily: 'var(--font-body)',
            }}
            data-testid="dashboard-subtitle"
          >
            {t('dashboard.subtitle')}
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6" data-testid="dashboard-error">
            <AlertTitle>{t('dashboard.error.title')}</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>
                {error instanceof Error
                  ? error.message
                  : t('dashboard.error.message')}
              </p>
              {error instanceof Error && error.message.includes('401') && (
                <div className="mt-2">
                  <p className="text-sm font-medium mb-1">{t('dashboard.error.sessionInvalid')}</p>
                  <ul className="text-sm list-disc list-inside space-y-1">
                    <li>{t('dashboard.error.signOutAndBackIn')}</li>
                    <li>{t('dashboard.error.refreshPage')}</li>
                    <li>{t('dashboard.error.clearCookies')}</li>
                  </ul>
                </div>
              )}
              {error instanceof Error && error.message.includes('403') && (
                <div className="mt-2">
                  <p className="text-sm font-medium mb-1">{t('dashboard.error.accessDenied')}</p>
                  <ul className="text-sm list-disc list-inside space-y-1 mb-2">
                    <li>{t('dashboard.error.restaurantNotSetup')}</li>
                    <li>{t('dashboard.error.organizationNotLinked')}</li>
                    <li>{t('dashboard.error.contactAdmin')}</li>
                  </ul>
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await clerk.signOut()
                      // Use replace to avoid adding to history
                      window.location.replace('/admin/login')
                    } catch (error) {
                      // If signOut fails, force clear everything
                      console.warn('Sign out failed, forcing session clear:', error)
                      clearSession()
                    }
                  }}
                >
                  {t('dashboard.error.clearSession')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.reload()}
                >
                  {t('dashboard.error.refresh')}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Metrics Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6"
          style={{
            gap: 'var(--spacing-lg)',
            marginBottom: 'var(--spacing-lg)',
          }}
          data-testid="dashboard-metrics-grid"
        >
          {isSuperAdmin ? (
            <MetricCard
              title={t('dashboard.metrics.totalRestaurants')}
              value={data?.restaurants.count}
              icon={Store}
              isLoading={isLoading}
              data-testid="metric-restaurants"
            />
          ) : data?.restaurants.info ? (
            <MetricCard
              title={t('dashboard.metrics.restaurant')}
              value={data.restaurants.info.name}
              icon={Store}
              isLoading={isLoading}
              data-testid="metric-restaurant-info"
            />
          ) : null}
          <MetricCard
            title={`${t('dashboard.metrics.totalBranches')}${scope === 'platform' ? t('dashboard.metrics.scope.all') : scope === 'restaurant' ? t('dashboard.metrics.scope.restaurant') : t('dashboard.metrics.scope.branch')}`}
            value={data?.branches.count}
            icon={Building2}
            isLoading={isLoading}
            data-testid="metric-branches"
          />
          <MetricCard
            title={`${t('dashboard.metrics.menuItems')}${scope === 'platform' ? t('dashboard.metrics.scope.all') : scope === 'restaurant' ? t('dashboard.metrics.scope.restaurant') : t('dashboard.metrics.scope.branch')}`}
            value={data?.menuItems.count}
            icon={UtensilsCrossed}
            isLoading={isLoading}
            data-testid="metric-menu-items"
          />
          <MetricCard
            title={`${t('dashboard.metrics.activeSessions')}${scope === 'platform' ? t('dashboard.metrics.scope.all') : scope === 'restaurant' ? t('dashboard.metrics.scope.restaurant') : t('dashboard.metrics.scope.branch')}`}
            value={data?.activeSessions.count}
            icon={Users}
            isLoading={isLoading}
            data-testid="metric-active-sessions"
          />
          <MetricCard
            title={`${t('dashboard.metrics.ordersToday')}${scope === 'platform' ? t('dashboard.metrics.scope.all') : scope === 'restaurant' ? t('dashboard.metrics.scope.restaurant') : t('dashboard.metrics.scope.branch')}`}
            value={data?.metrics.ordersToday.count}
            icon={ShoppingCart}
            isLoading={isLoading}
            data-testid="metric-orders-today"
          />
          <MetricCard
            title={`${t('dashboard.metrics.revenueToday')}${scope === 'platform' ? t('dashboard.metrics.scope.all') : scope === 'restaurant' ? t('dashboard.metrics.scope.restaurant') : t('dashboard.metrics.scope.branch')}`}
            value={
              data?.metrics.revenueToday.amount
                ? `$${data.metrics.revenueToday.amount.toFixed(1)}`
                : undefined
            }
            icon={DollarSign}
            isLoading={isLoading}
            data-testid="metric-revenue-today"
          />
        </div>

        {/* Recent Orders */}
        <div data-testid="dashboard-recent-orders-section">
          <RecentOrders
            orders={data?.recentOrders}
            isLoading={isLoading}
            data-testid="recent-orders"
          />
        </div>
      </div>
    </AdminLayout>
  )
}
